import React, { useState, useEffect, useContext } from 'react';
import { Space, Form, Button, Row, Table, Checkbox, Popconfirm, Input, Typography, message, InputNumber, } from 'antd';
import { DeleteOutlined, SaveOutlined, PlusOutlined, DeleteFilled, } from '@ant-design/icons';
import { Modal } from '../../../../UI/Modal/Modal.component';
import { Section } from '../../../../UI/Section.component';
import { StationContext } from '../../../../../context/station-context';
import { deleteVLAN, updateVLAN, saveVLAN } from '../../../../../services/StationService';
import { DeviceRestrictedInput, getInputRules } from '../DeviceRestrictedInput.component';

const { Title } = Typography;

const PAGE_SIZE = 10;

const EditableCell = ({ editing, dataIndex, title, inputType, record, index, rules, children, ...restProps }) => {

	let inputNode;
	let valuePropName = 'value';

	switch(inputType){
		case 'checkbox':
			inputNode = <Checkbox/>;
			valuePropName = 'checked';
			break;
		case 'number': 
			inputNode = <InputNumber/>
			break;
		default: 
			inputNode = <Input/>
	}

	return (
		<td {...restProps}>
			{ editing ? 
				<Form.Item 
					name={dataIndex}
					valuePropName={valuePropName}
					style={{margin: 0}}
					rules={rules}
					>
					{inputNode}
				</Form.Item>
				: 
				children
			}
		</td>
	);
};

export const ManageVLANModal = ({dataSource, displayModal, setDisplayModal, ...other}) => {

	const [stationState, stationDispatch] = useContext(StationContext);
	const { VLANTags, } = stationState.station;
	
	const [editVLANForm] = Form.useForm();
	const [addVLANForm] = Form.useForm();

	const [data, setNewData] = useState(VLANTags);
	const [count, setCount] = useState(data?.length + 1);
	const [currentPage, setCurrentPage] = useState(1);
	const [editingKey, setEditingKey] = useState('');
	const [visible, setVisible] = useState(false);
	
	const isEditing = (record) => {
		return record.id === editingKey;
	};

	useEffect(() => {
		setNewData(VLANTags);
	}, [VLANTags, editingKey, data]);

	const columns = [
		{
			title: 'VLAN ID',
			dataIndex: 'vlanID',
			key: 'vlanID',
			fixed: 'left',
			width: "12ch",
			editable: true,
			inputType: 'number', 
			rules: [
				{ required: true, message: 'VLAN ID is required' },
                { type: 'number', min: 1, max: 4095, message: 'VLAN ID must be between 1 and 4095'}
			]
		},
		{
			title: 'VLAN Tag',
			dataIndex: 'tag',
			key: 'tag',
			editable: true,
			inputType: 'text',
			rules: getInputRules('tag')
		},
		{
			title: 'CIDR',
			dataIndex: 'CIDR',
			key: 'CIDR',
			editable: true,
			inputType: 'text',
			rules: getInputRules('CIDR')
		},
		{
			title: 'Local IP',
			dataIndex: 'localip',
			key: 'localip',
			editable: true,
			inputType: 'text',
			rules: getInputRules('localip')
		},
		{
			title: 'Masquerade',
			dataIndex: 'masquerade',
			key: 'masquerade',
			width: 150,
			align: 'center',
			editable: true,
			inputType: 'checkbox',
			render: (value, _) => {
				return ( <Checkbox disabled={true} checked={value}/> );
			},
		},
		{
			title: '',
			dataIndex: 'action',
			key: 'action',
			width: 160,
			render: (_, record) => {
				const editable = isEditing(record);
				const btnSize = 'small';
				return editable ? (
					<Space>
						<Button size={btnSize} onClick={() => save(record.id)} type="secondary" className="btn__secondary--success" icon={<SaveOutlined/>}></Button>
						<Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
							<Button size={btnSize} danger type="primary" icon={<DeleteOutlined/>}></Button>
						</Popconfirm>
						<Button size={btnSize} onClick={cancel}>Cancel</Button>
					</Space>
					) : (
					<Typography.Link disabled={editingKey !== ''} onClick={() => edit(record)}>
						Edit
					</Typography.Link>
				);
			},
		},
	];

	const handleDelete = (record) => {
		deleteVLAN({id: record.id}).then( function(response) {
			if( response.result ) {
				stationDispatch({
					type: 'DELETE_VLAN_TAG',
					payload: record.id,
				});
				message.success({
					content: <span><strong>VLAN ID: {record.vlanID}</strong> has been successfully deleted.</span>,
					duration: 4, 
					icon: <DeleteFilled style={{color: "red"}}/>
				});
			}
		})
		setEditingKey('');
	}

	const edit = (record) => {
		editVLANForm.setFieldsValue({...record})
		setEditingKey(record.id);
	};

	const cancel = () => {
		setEditingKey('');
	};

	const save = async (id) => {
		try {
			const row = await editVLANForm.validateFields();
			const newData = [...data];
			const index = newData.findIndex((item) => id === item.id);

			if (index > -1) {
				const item = newData[index];
				newData.splice(index, 1, { ...item, ...row });
				setNewData(newData);
				Object.assign(newData[index], {oldVlanID: dataSource[index].vlanID});
				updateVLAN(newData[index]).then(function(response) {
					stationDispatch({
							type: 'UPDATE_VLAN_TAG', 
							payload: newData[index],
						})
					if( response.result ) {
						message.success("VLAN has been updated", 4);
					}
				})
				setEditingKey('');
			} else {
				newData.push(row);
				setNewData(newData);
				setEditingKey('');
			}
		} catch (errInfo) {
			// console.log('Validate Failed:', errInfo);
		}
	};

	const mergedColumns = columns.map((col) => {
		if (!col.editable) {
			return col;
		}
		
		return {
			...col,
			onCell: (record) => ({
				record,
				dataIndex: col.dataIndex,
				title: col.title,
				editing: isEditing(record),
				inputType: col.inputType,
				rules: col.rules,
			}),
		};
	});

	const addVLANHandler = (values) => {		

		const newVLAN = {
			ownerID: stationState.station.id,
			...values
		}

		saveVLAN(newVLAN).then( function( response ){
			if ( response.result ) {
				stationDispatch({
					type: 'ADD_VLAN_TAG', 
					payload: {...newVLAN, id: response.vlan.id},
				})
				message.success(<span><strong>VLAN ID: {values.vlanID}</strong> has been successfully created.</span>, 4);
				setVisible(false);
			}
		});

		setCount(count+1);
		if (data.length > 1){
			setCurrentPage(Math.ceil(data.length + 1 / PAGE_SIZE));
		} else {
			setCurrentPage(1);
		}
	}

	const addVLANFormEl = ( 
		<Form 
			autoComplete='off'
			form={addVLANForm}
			name="add-vlan-form" 
			layout="vertical" 
			style={{display: 'flex', alignItems: 'flex-start', gap: 20, }} 
			onFinish={addVLANHandler}
			>
			<DeviceRestrictedInput label="VLAN ID" name="vlanID">
				<Input/>
			</DeviceRestrictedInput>
			<DeviceRestrictedInput label="VLAN Tag" name="tag">
				<Input allowClear/>
			</DeviceRestrictedInput>
			<DeviceRestrictedInput label="CIDR" name="CIDR">
				<Input allowClear/>
			</DeviceRestrictedInput>
			<DeviceRestrictedInput label="Local IP" name="localip" >
				<Input allowClear/>
			</DeviceRestrictedInput>
			<Form.Item label={' '}>
				<Space>
					<Button icon={<PlusOutlined/>} type="primary" htmlType='submit'>Add VLAN</Button>
					<Button onClick={()=> setVisible(!visible)}>Cancel</Button>
				</Space>
			</Form.Item>
		</Form>
	);

	const paginationOnChange = (page) => {
		setEditingKey('');
		setCurrentPage(page)
	}

	const getRowClassName = (row) => {
		if(editingKey === row.id){
			return ('editable-row editing-row')
		}
		return ('editable-row');
	}

	const displayAddVLANForm = () => {
		setVisible(true);
		setEditingKey('');
		addVLANForm.resetFields();
	}

	return(
		<>
			<Modal
				visible={displayModal}
				setVisible={setDisplayModal}
				actionGroup={false}
				width={"min(75vw, 1200px)"}
			>
				<div className="vlan__manage-vlans">
					<Row justify="space-between" style={{marginBottom: 20}}>
						<Title level={3}>Manage VLANs</Title>
						<Space>
							<Button onClick={()=> displayAddVLANForm()} icon={<PlusOutlined/>} type="primary" style={{display: visible ? 'none' : 'block',}}> Add VLAN</Button>
						</Space>
					</Row>
					<Section type="secondary" style={{paddingBottom: 10, display: visible ? 'block' : 'none'}} >
						{addVLANFormEl}
					</Section>
					<Form form={editVLANForm} component={false} autoComplete='off'>
						<Table 
							size="small"
							components={{
								body: {
									cell: EditableCell,
								},
							}}
							dataSource={data} 
							rowKey='id' 
							columns={mergedColumns}
							rowClassName={(row) => getRowClassName(row)}
							scroll={{
								x: 900,
							}} 
							pagination = {{
								size: "small",
								defaultCurrent: 1,
								current: currentPage,
								defaultPageSize: 10,
								showTotal: (total) => `Total ${total} items`,
								showQuickJumper: true,
								showSizeChanger: false,
								onChange: (page) => {paginationOnChange(page)},
							}}
						/>
					</Form>
				</div>
			</Modal>
		</>
	);
}